import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { UNIT_MODELS, BRAND } from "@/content";
import { Phone, Loader2, Send, Check, Mail } from "lucide-react";
import { WhatsAppGlyph, whatsappLink } from "@/components/WhatsApp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.trim();
const API = BACKEND_URL ? `${BACKEND_URL}/api` : null;
const GOOGLE_SHEETS_API = "/api/google-sheets-lead";

export default function LeadForm() {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    unit_model: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const onSelect = (e) => setForm((f) => ({ ...f, unit_model: e.detail }));
    window.addEventListener("muyassr:select-unit", onSelect);
    return () => window.removeEventListener("muyassr:select-unit", onSelect);
  }, []);

  const validate = () => {
    const e = {};
    if (!form.full_name.trim() || form.full_name.trim().length < 2) e.full_name = "الرجاء إدخال الاسم الكامل";
    const phoneDigits = form.phone.replace(/\D/g, "");
    if (phoneDigits.length < 8) e.phone = "رقم الجوال غير صحيح";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "بريد إلكتروني غير صحيح";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        unit_model: form.unit_model || null,
        message: form.message.trim() || null,
        source: "landing-page",
      };
      if (!API) {
        const res = await fetch(GOOGLE_SHEETS_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...payload,
            id: window.crypto?.randomUUID?.() || `${Date.now()}`,
            timestamp: new Date().toISOString(),
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.ok) {
          throw new Error(data.error || "تعذّر إرسال الطلب إلى Google Sheets.");
        }
        toast.success("تم استلام طلبك بنجاح");
        setSubmitted(true);
        if (window.fbq) window.fbq("track", "Lead");
        if (window.gtag) window.gtag("event", "generate_lead", { method: "form" });
        return;
      }
      const res = await axios.post(`${API}/leads`, payload, { timeout: 12000 });
      if (res.data?.success) {
        toast.success(res.data.message || "تم استلام طلبك بنجاح");
        setSubmitted(true);
        // Fire Meta Pixel + GA events if available
        if (window.fbq) window.fbq("track", "Lead");
        if (window.gtag) window.gtag("event", "generate_lead", { method: "form" });
      } else {
        toast.error("تعذّر إرسال الطلب. حاول مرة أخرى.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "حدث خطأ غير متوقع. حاول مرة أخرى.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="form"
      data-testid="section-form"
      className="relative py-24 lg:py-36 bg-brand-olive-deep text-brand-cream"
    >
      <div className="absolute inset-0 opacity-[0.04] bg-grid-soft" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: pitch */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <span
                className="block text-xs text-brand-accent mb-5"
              >
                سجّل اهتمامك
              </span>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl mb-6 leading-tight" data-testid="form-headline">
                تواصل معنا ليصلك المزيد
              </h2>
              <p className="text-base lg:text-lg text-brand-cream/80 leading-loose mb-10 max-w-md">
                اترك بياناتك وسيتواصل معك مستشارنا العقاري لتقديم العرض الأنسب لتطلعاتك.
              </p>

              <div className="space-y-4">
                <a
                  href={whatsappLink(form.unit_model, "form-sidebar")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-brand-cream/5 border border-brand-cream/10 hover:bg-brand-cream/10 transition-colors group"
                  data-testid="form-whatsapp-link"
                >
                  <span className="w-11 h-11 rounded-full bg-[#25D366] flex items-center justify-center text-white">
                    <WhatsAppGlyph size={18} />
                  </span>
                  <div>
                    <div className="text-xs text-brand-cream/60">واتساب</div>
                    <div className="text-base font-medium" dir="ltr">{BRAND.phoneDisplay}</div>
                  </div>
                </a>

                <a
                  href={BRAND.telUrl}
                  className="flex items-center gap-4 p-4 bg-brand-cream/5 border border-brand-cream/10 hover:bg-brand-cream/10 transition-colors group"
                  data-testid="form-tel-link"
                >
                  <span className="w-11 h-11 rounded-full bg-brand-accent flex items-center justify-center text-brand-ink">
                    <Phone size={18} />
                  </span>
                  <div>
                    <div className="text-xs text-brand-cream/60">اتصال هاتفي</div>
                    <div className="text-base font-medium" dir="ltr">{BRAND.phoneDisplay}</div>
                  </div>
                </a>

                <a
                  href={`mailto:${BRAND.email}`}
                  className="flex items-center gap-4 p-4 bg-brand-cream/5 border border-brand-cream/10 hover:bg-brand-cream/10 transition-colors"
                  data-testid="form-email-link"
                >
                  <span className="w-11 h-11 rounded-full bg-brand-cream/15 flex items-center justify-center text-brand-cream">
                    <Mail size={18} />
                  </span>
                  <div>
                    <div className="text-xs text-brand-cream/60">البريد الإلكتروني</div>
                    <div className="text-base font-medium">{BRAND.email}</div>
                  </div>
                </a>
              </div>
            </motion.div>
          </div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            {submitted ? (
              <div className="bg-brand-cream text-brand-ink p-10 lg:p-14 rounded-sm" data-testid="form-success">
                <div className="w-16 h-16 rounded-full bg-brand-olive/10 text-brand-olive flex items-center justify-center mb-6">
                  <Check size={28} strokeWidth={2} />
                </div>
                <h3 className="text-3xl font-serif mb-3">شكرًا لتسجيلك</h3>
                <p className="text-brand-mute leading-loose mb-8">
                  وصلتنا بياناتك وسيتواصل معك مستشار مُيسر العقاري في أقرب وقت. للسرعة، تواصل عبر واتساب الآن:
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href={whatsappLink(form.unit_model, "form-success")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1eb958] text-white px-6 py-3 text-sm font-medium rounded-sm transition-colors"
                    data-testid="form-success-whatsapp"
                  >
                    <WhatsAppGlyph size={16} />
                    تواصل عبر واتساب الآن
                  </a>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setForm({ full_name: "", phone: "", email: "", unit_model: "", message: "" });
                    }}
                    className="text-sm text-brand-olive hover:text-brand-olive-dark underline underline-offset-4 px-3"
                    data-testid="form-reset-btn"
                  >
                    تسجيل جديد
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="bg-brand-cream text-brand-ink p-8 lg:p-12 rounded-sm space-y-6"
                data-testid="lead-form"
                noValidate
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    label="الاسم الكامل"
                    name="full_name"
                    value={form.full_name}
                    onChange={(v) => setForm({ ...form, full_name: v })}
                    error={errors.full_name}
                    placeholder="مثال: محمد عبدالله"
                    required
                    testid="input-full-name"
                  />
                  <Field
                    label="رقم الجوال"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v })}
                    error={errors.phone}
                    placeholder="05XXXXXXXX"
                    dir="ltr"
                    required
                    testid="input-phone"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    label="البريد الإلكتروني (اختياري)"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={(v) => setForm({ ...form, email: v })}
                    error={errors.email}
                    placeholder="email@example.com"
                    dir="ltr"
                    testid="input-email"
                  />
                  <div>
                    <label className="block text-xs text-brand-mute mb-2">
                      النموذج المفضل
                    </label>
                    <select
                      value={form.unit_model}
                      onChange={(e) => setForm({ ...form, unit_model: e.target.value })}
                      className="w-full bg-transparent border-b border-brand-border focus:border-brand-olive py-3 text-base outline-none transition-colors"
                      data-testid="input-unit-model"
                    >
                      <option value="">اختياري — حدد لاحقًا</option>
                      {UNIT_MODELS.map((m) => (
                        <option key={m.code} value={m.code}>
                          نموذج {m.code} — {m.area}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-brand-mute mb-2">
                    ملاحظات (اختياري)
                  </label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={3}
                    className="w-full bg-transparent border-b border-brand-border focus:border-brand-olive py-3 text-base outline-none transition-colors resize-none"
                    placeholder="أخبرنا باحتياجاتك..."
                    data-testid="input-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-brand-olive hover:bg-brand-olive-dark disabled:opacity-60 disabled:cursor-not-allowed text-brand-cream px-10 py-4 text-base font-medium rounded-sm transition-all duration-300 hover:-translate-y-0.5"
                  data-testid="form-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      جارٍ الإرسال...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      إرسال الطلب
                    </>
                  )}
                </button>

                <p className="text-xs text-brand-mute">
                  بإرسالك الطلب فإنك توافق على تواصلنا معك عبر القنوات المتاحة.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, error, placeholder, type = "text", required, dir, testid }) {
  return (
    <div>
      <label className="block text-xs text-brand-mute mb-2" htmlFor={name}>
        {label} {required && <span className="text-brand-accent">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        dir={dir}
        className={`w-full bg-transparent border-b ${
          error ? "border-red-500" : "border-brand-border focus:border-brand-olive"
        } py-3 text-base outline-none transition-colors placeholder:text-brand-mute/50`}
        data-testid={testid}
      />
      {error && (
        <p className="mt-1 text-xs text-red-600" data-testid={`${testid}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}

